import {
  MergedScheduledPayment,
  NameOfFrequency,
  ScheduledPaymentsRequest,
  ScheduledTransactionsRequest,
  legacyToMergedScheduledPayment,
  toMergedScheduledPayment,
} from 'vopay';
import { useState } from 'react';
import { api } from '@/services/api.config';
import { useClientData } from '@/providers/ClientData.provider';

// Shared by every screen that maps the scheduled-transactions filter form onto
// a ScheduledPaymentsRequest, so the value vocabulary lives in one place.
export function toScheduledPaymentsFilters(filters: {
  scheduleType?: string;
  transactionType?: string;
  frequency?: NameOfFrequency | 'all' | '';
  status?: string;
}): ScheduledPaymentsRequest {
  const request: ScheduledPaymentsRequest = {};

  if (filters.scheduleType === 'single' || filters.scheduleType === 'recurring') {
    request.ScheduleType = filters.scheduleType;
  }

  if (filters.transactionType === 'fund' || filters.transactionType === 'withdraw') {
    request.PaymentType = filters.transactionType === 'fund' ? 'debit' : 'credit';
  }

  if (filters.frequency && filters.frequency !== 'all') {
    request.Repeats = filters.frequency;
  }

  if (filters.status === 'in progress' || filters.status === 'cancelled' || filters.status === 'completed') {
    request.Status = filters.status;
  }

  return request;
}

// NameOfFrequency is sent in the /scheduled-payment vocabulary — the endpoint's
// validator accepts it and (as of the earthnode ID-13095 change) translates it
// to the stored legacy vocabulary so it matches legacy rows. 'custom' is the
// one value the validator rejects, so it is filtered client-side instead.
function toLegacyScheduledTransactionsParams(params: ScheduledPaymentsRequest): ScheduledTransactionsRequest {
  return {
    StartDate: params.StartDate,
    EndDate: params.EndDate,
    SearchText: params.SearchText,
    ...(params.ScheduleType ? { Frequency: params.ScheduleType } : {}),
    ...(params.PaymentType ? { ScheduleTransactionType: params.PaymentType === 'debit' ? 'fund' : 'withdraw' } : {}),
    ...(params.Repeats && params.Repeats !== 'custom' ? { NameOfFrequency: params.Repeats } : {}),
    ...(params.Status ? { ScheduledTransactionStatus: params.Status } : {}),
    ...(params.ClientAccountID ? { ClientAccountID: params.ClientAccountID } : {}),
    Limit: params.Limit,
    Offset: params.Offset,
  };
}

/**
 * Scheduled payments for the account portal, read from both sources:
 * the new /scheduled-payment endpoint (all rails, VIOScheduledPayments only)
 * and the legacy /eft|ach/scheduled-transactions endpoint, which still holds
 * schedules that predate VIOScheduledPayments (ID-13095).
 */
export default function useFetchScheduledPayments() {
  const { response: clientData } = useClientData();
  const [fetching, setFetching] = useState(false);

  function fetchLegacyScheduledTransactions(params: ScheduledTransactionsRequest) {
    return clientData.Country === 'US'
      ? api.fetchACHScheduledTransactions(params)
      : api.fetchScheduledTransactions(params);
  }

  // The legacy endpoint merges old + new tables server-side and tags rows that
  // came from the new table with TransactionType — those are duplicates of what
  // /scheduled-payment already returned, so only the untagged rows are kept
  // (a page can therefore hold fewer legacy rows than its Limit).
  // The legacy source supplements /scheduled-payment, so its failure must not
  // take down the whole list: it is logged and degrades to an empty set.
  async function getLegacyScheduledPayments(
    params: ScheduledPaymentsRequest,
  ): Promise<{ payments: MergedScheduledPayment[]; numTotalRecords: number }> {
    try {
      const legacyRes = await fetchLegacyScheduledTransactions(toLegacyScheduledTransactionsParams(params));
      if (!legacyRes.Success) throw new Error(legacyRes.ErrorMessage);

      // The frequency filter also runs client-side (on the normalized Repeats):
      // 'custom' is never sent to the legacy endpoint (its validator rejects
      // it), so this is where a 'custom' filter takes effect.
      const payments = legacyRes.ScheduledTransactions.filter((transaction) => !transaction.TransactionType)
        .map((transaction) => legacyToMergedScheduledPayment(transaction, clientData.Country))
        .filter((payment) => !params.Repeats || payment.Repeats === params.Repeats);

      return { payments, numTotalRecords: legacyRes.PaginationMetaData?.TotalRecords ?? payments.length };
    } catch (error) {
      console.error(error);

      return { payments: [], numTotalRecords: 0 };
    }
  }

  // Both sources are queried with the same Limit/Offset and paginate
  // independently: a page can hold up to 2×Limit rows and the date sort only
  // holds within a page.
  async function fetchScheduledPayments(params: ScheduledPaymentsRequest) {
    try {
      setFetching(true);

      const [newRes, legacy] = await Promise.all([
        api.fetchScheduledPayments(params),
        getLegacyScheduledPayments(params),
      ]);

      if (!newRes.Success) throw new Error(newRes.ErrorMessage);

      const scheduledPayments = [...newRes.ScheduledPayments.map(toMergedScheduledPayment), ...legacy.payments].sort(
        (a, b) => b.StartDateTime.localeCompare(a.StartDateTime),
      );

      // The page count follows whichever source has more records.
      const numTotalRecords = Math.max(
        newRes.PaginationMetaData?.TotalRecords ?? newRes.ScheduledPayments.length,
        legacy.numTotalRecords,
      );

      return { scheduledPayments, numTotalRecords };
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  // Details: a list row passes its source via isLegacy so only the matching
  // endpoint is queried (legacy and new IDs are independent auto-increments, so
  // the same number can exist in both tables). A deep link has no source, so
  // the new endpoint is tried first with the legacy endpoint as the fallback
  // for records that predate VIOScheduledPayments.
  async function fetchScheduledPaymentDetails(
    scheduledPaymentsID: string,
    isLegacy?: boolean,
  ): Promise<MergedScheduledPayment | undefined> {
    try {
      setFetching(true);

      if (isLegacy !== true) {
        const newRes = await api.fetchScheduledPayments({ ScheduledPaymentsID: scheduledPaymentsID });
        if (!newRes.Success) throw new Error(newRes.ErrorMessage);
        if (newRes.ScheduledPayments.length > 0) return toMergedScheduledPayment(newRes.ScheduledPayments[0]);
        if (isLegacy === false) return undefined;
      }

      const legacyRes = await fetchLegacyScheduledTransactions({ ScheduledTransactionID: scheduledPaymentsID });
      if (!legacyRes.Success) throw new Error(legacyRes.ErrorMessage);
      const legacyTransaction = legacyRes.ScheduledTransactions.find((transaction) => !transaction.TransactionType);

      return legacyTransaction ? legacyToMergedScheduledPayment(legacyTransaction, clientData.Country) : undefined;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      setFetching(false);
    }
  }

  return { fetching, fetchScheduledPayments, fetchScheduledPaymentDetails };
}
